import type React from 'react';
import {
  type ComponentType,
  type FunctionComponent,
  PureComponent,
  type ReactNode,
} from 'react';

import classNames from 'classnames';

import type { MetadataValues } from '@iconik-io/iconik-api/src/schema_patched/metadata';
import type { AssetsGetShareSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_METADATA_VIEWS,
  OBJECT_TYPE_SEGMENTS,
  type SearchObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';

import AiMetadataButton from '../../components/ai_metadata_button/ai_metadata_button';
import EntityMetadata, {
  type EntityMetadataProps,
} from '../../components/entity_metadata/entity_metadata';
import { NavigationPrompt } from '../../components/navigation_prompt/navigation_prompt';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import permissionsManager, {
  type PermissionManagerInjectedProps,
} from '../../components/permissions_manager/permissions_manager';
import SearchPod from '../../components/search_pod_detailed/search_pod_detailed';
import { UNSAVED_CHANGES_MESSAGE } from '../../constants/data';
import { METADATA } from '../../constants/help_urls';
import { DEFAULT_CATEGORY_NAME } from '../../constants/metadata';
import {
  ASSET_PAGE_ROUTE,
  SHARE_ASSET_PAGE_ROUTE,
} from '../../constants/routes';
import { METADATA_FILL_METHOD } from '../../ducks/analytics_tracking/metadata_event_properties';
import type { MetadataCategoryObjectType } from '../../ducks/pages/admin_metadata_categories/types';
import { getSelectedMetadataViewKey } from '../../ducks/selected_metadata/utils';
import type { AppConnectedProps } from '../../store/types';
import type { PermissionValues } from '../../types/common';
import { targetElement } from '../../utils/event_target';
import { getChangedFieldKeys } from '../../utils/format_metadata_values';
import AssetTitle from './asset_title/asset_title';
import connector from './connector';

import styles from './asset_metadata.module.css';

function findAncestor(el: Element | null, cls: string): Element | null {
  while ((el = el?.parentElement ?? null) && !el.classList.contains(cls)) {}
  return el;
}

type ManagerProps = EntityMetadataProps & PermissionManagerInjectedProps;
const EntityMetadataPermissionsManager: FunctionComponent<ManagerProps> = (
  props,
) => {
  const updateProps = {
    ...props,
    disabled: props.disabled || !props.permissionsRolesMerge?.write,
  };

  return <EntityMetadata {...updateProps} />;
};

const EntityMetadataPermissionsManagerContainer = permissionsManager(
  EntityMetadataPermissionsManager,
);

type Props = AppConnectedProps<typeof connector> & {
  entity?: SearchObjectType;
  categoryName: string | undefined;
  componentClass?: string;
  buttonsComponent?: ComponentType<EntityMetadataProps>;
  share?: AssetsGetShareSchemaWithIdNullable;
  permissions?: PermissionValues;
  metadataKey: string;
};

type State = {
  categoryName: {
    [objectType in MetadataCategoryObjectType]: string | undefined;
  };
  viewId?: string;
  editMode?: boolean;
  notificationUpdate?: boolean;
  loading?: boolean;
  submitting?: boolean;
  selectedCategoryObjectType: MetadataCategoryObjectType;
};

const getWritePermission = (
  canWriteMetadataValues: boolean,
  entityPermissions?: PermissionValues,
) =>
  entityPermissions ? canWriteMetadataValues && entityPermissions?.write : true;

const defaultCategories: {
  [objectType in MetadataCategoryObjectType]: string | undefined;
} = {
  [OBJECT_TYPE_ASSETS]: undefined,
  [OBJECT_TYPE_COLLECTIONS]: undefined,
  [OBJECT_TYPE_SEGMENTS]: undefined,
};

class AssetMetadataPanel extends PureComponent<Props, State> {
  state: State = {
    categoryName: defaultCategories,
    selectedCategoryObjectType: this.props.item?.object_type ?? 'assets',
  };

  getSelectedCategoryName = (): string => {
    const categoryObjectType: keyof typeof this.state.categoryName =
      this.state.selectedCategoryObjectType;
    const stateCategoryName = categoryObjectType
      ? this.state.categoryName[categoryObjectType]
      : undefined;
    return (
      stateCategoryName ?? this.props.categoryName ?? DEFAULT_CATEGORY_NAME
    );
  };

  handleCategoryChange = (
    categoryName: string,
    sideEffect: boolean,
    categoryObjectType: MetadataCategoryObjectType,
  ): void => {
    this.setState({
      categoryName: {
        ...this.state.categoryName,
        [categoryObjectType]: categoryName,
      },
      selectedCategoryObjectType: categoryObjectType,
      viewId: undefined,
    });
    this.props.metadataEntitySelectView({
      categoryObjectType,
      categoryName,
      viewId: undefined,
    });
    // Clear form dirty state when category changes since data is reset
    this.setMetadataPanelDirty(false);
    const { isLatestVersion, entity, roles, entityPermissions } = this.props;
    const hasWritePermission = getWritePermission(
      roles.canWriteMetadataValues,
      entityPermissions,
    );
    if (
      !sideEffect &&
      (isLatestVersion || entity === OBJECT_TYPE_COLLECTIONS) &&
      hasWritePermission
    ) {
      this.setState({ editMode: true });
    }
  };

  handleViewChange = (viewId: string): void => {
    if (viewId !== this.state.viewId) {
      const selectedCategoryName = this.getSelectedCategoryName();

      let viewIdToSelect;
      if (this.state.viewId) {
        viewIdToSelect = viewId;
      } else {
        const key = getSelectedMetadataViewKey(
          this.state.selectedCategoryObjectType,
          selectedCategoryName,
        );
        viewIdToSelect = this.props.selectedMetadataViewId[key] ?? viewId;
      }

      this.props.metadataEntitySelectView({
        categoryObjectType: this.state.selectedCategoryObjectType,
        categoryName: selectedCategoryName,
        viewId: viewIdToSelect,
      });

      // Clear form dirty state when view changes since new data is fetched
      this.setMetadataPanelDirty(false);

      this.fetchData(viewIdToSelect);
    }
  };

  fetchData = (viewId: string): void => {
    const { item, openedVersionId } = this.props;
    if (!item) {
      return;
    }
    this.setState({ viewId, loading: true });
    this.props
      .getMetadataValues(item.id, {
        versionId: openedVersionId,
        viewId,
        objectType: this.state.selectedCategoryObjectType,
      })
      .finally(() => this.setState({ loading: false }));
  };

  componentDidUpdate(prevProps: Readonly<Props>): void {
    // handle notifications
    const {
      metadataValuesClearById,
      awaitingMetadataValues,
      metadataValuesAddAwaiting,
      item,
      openedVersionId,
      route,
      metadataKey,
    } = this.props;
    const newValuesLength = Object.keys(awaitingMetadataValues).length;
    const valuesLength = Object.keys(prevProps.awaitingMetadataValues).length;
    if (newValuesLength > 0 && valuesLength === 0 && !this.state.submitting) {
      if (this.state.editMode) {
        this.setState({ notificationUpdate: true });
      } else {
        metadataValuesAddAwaiting();
      }
    }

    if (valuesLength > 0 && newValuesLength === 0) {
      this.setState({ notificationUpdate: false });
    }

    const isMetadataPanelDirty = !this.props.panelsPristine[metadataKey];

    if (
      item?.id !== prevProps.item?.id ||
      openedVersionId !== prevProps.openedVersionId
    ) {
      this.setState({ viewId: undefined });
      if (item?.object_type) {
        // Keep the segments category when navigating between subclips, reset to the item's type otherwise
        const selectedCategoryObjectType =
          item.type === 'SUBCLIP'
            ? this.state.selectedCategoryObjectType
            : item.object_type;
        this.setState({ selectedCategoryObjectType });
      }
      if (this.state.categoryName === defaultCategories) {
        this.setState({
          categoryName: defaultCategories,
        });
      }
    }

    if (item?.id !== prevProps.item?.id) {
      if (isMetadataPanelDirty && route !== ASSET_PAGE_ROUTE) {
        // This inner nest helps prevent prompt from showing
        // when click-selecting on search items in edit mode on grid pages.
        this.setMetadataPanelDirty(false);
      }
    } else if (item && openedVersionId !== prevProps.openedVersionId) {
      metadataValuesClearById(item.id);
    }

    if (prevProps.route !== route) {
      // Disables editMode when changing route (of course after warning was accepted).
      this.setMetadataPanelDirty(false);
      this.setState({ editMode: false, loading: true }, () => {
        this.setState({ loading: false });
      });
    }
  }

  componentWillUnmount() {
    this.setMetadataPanelDirty(false);
  }

  onEdit = (event: React.MouseEvent<HTMLElement>): void => {
    const hasWritePermission = getWritePermission(
      this.props.roles.canWriteMetadataValues,
      this.props.entityPermissions,
    );

    const { isLatestVersion, entity } = this.props;
    // prevent editing metadata on older versions
    if (
      this.state.editMode ||
      (!isLatestVersion && entity !== OBJECT_TYPE_COLLECTIONS)
    ) {
      return;
    }

    // prevent edit mode when clicking available buttons in read mode
    const element = targetElement(event.target);
    if (!element) {
      return;
    }
    if (!this.state.editMode && element.tagName === 'BUTTON') {
      return;
    }

    // prevent edit mode when clicking links in read mode
    if (!this.state.editMode && element.tagName === 'A') {
      return;
    }

    // this is a necessary hack to make possible focus the clicked field when the form is enabled
    const savedTarget = element;
    setTimeout(() => {
      const select = findAncestor(element, 'form-group');
      const combobox = select?.querySelector?.<HTMLElement>(
        'input:not([type="hidden"]), textarea',
      );
      if (combobox) {
        combobox.focus();
        return;
      }

      // when input is disabled we should the target will be one of its parents
      const childInput = savedTarget.querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea',
      );
      if (childInput) {
        childInput.focus();
        return;
      }

      if (savedTarget instanceof HTMLElement) {
        savedTarget.focus();
      }
    }, 100);

    if (hasWritePermission) {
      this.setState({ editMode: true });
    }
  };

  handleFormChange = (data: MetadataValues): void => {
    const { item, metadataValues } = this.props;
    if (!item) {
      return;
    }
    const originalValues = metadataValues[item.id] ?? {};
    const changedKeys = getChangedFieldKeys(data, originalValues);

    this.setMetadataPanelDirty(changedKeys.length > 0);
  };

  onRefresh = (): void => {
    this.setState({ notificationUpdate: false });
    this.props.metadataValuesAddAwaiting();
  };

  onCancelEdit = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    this.setMetadataPanelDirty(false);
    // remount the fields it's needed to clear their internal state
    this.setState({ editMode: false, loading: true }, () => {
      this.setState({ loading: false });
    });
  };

  submitMetadata = (values: MetadataValues): void => {
    const {
      entity,
      item,
      segmentTrackEvent,
      putMetadataValues,
      openedVersionId,
      metadataValues,
      metadataViews,
    } = this.props;
    const { viewId } = this.state;
    if (!viewId || !item) {
      return;
    }
    const originalValues = metadataValues[item.id] ?? {};
    const updatedFieldsCount = getChangedFieldKeys(
      values,
      originalValues,
    ).length;
    const totalFields = metadataViews[viewId]?.view_fields?.length ?? 0;

    this.setState({ submitting: true });
    putMetadataValues(item.id, viewId, values, entity, openedVersionId)
      .then(() => {
        segmentTrackEvent({
          eventName: 'Metadata Edited',
          properties: {
            Entity: entity,
            Bulk: false,
            'Fill method': METADATA_FILL_METHOD.USER_SET,
            'Total fields': totalFields,
            'Updated values': updatedFieldsCount,
            'AI suggested fields': null,
          },
        });
      })
      .finally(() => {
        this.setMetadataPanelDirty(false);
        this.setState({
          editMode: false,
          submitting: false,
        });
      });
  };

  setMetadataPanelDirty = (isMetadataPanelDirty: boolean) =>
    isMetadataPanelDirty
      ? this.props.setPanelDirtyById(this.props.metadataKey)
      : this.props.setPanelPristineById(this.props.metadataKey);

  render(): ReactNode {
    const {
      metadataValues,
      entityPermissions,
      breakpoint,
      componentClass,
      buttonsComponent,
      roles,
      share,
      route,
      item,
      openedVersionId,
      metadataKey,
      hasMultipleItemsSelected,
      withPreview,
    } = this.props;
    const values = (item ? metadataValues[item.id] : undefined) ?? {};
    const visible = roles.canReadMetadataViews && roles.canReadMetadataValues;

    const write = getWritePermission(
      roles.canWriteMetadataValues,
      entityPermissions,
    );
    // entityPermissions is asset permissions
    // EntityMetadataPermissionsManagerContainer handles metadata view permissions
    const isShare = route === SHARE_ASSET_PAGE_ROUTE;
    const EntityMetadataComponent = isShare
      ? EntityMetadata
      : EntityMetadataPermissionsManagerContainer;

    const selectedCategoryName = this.getSelectedCategoryName();
    const isMetadataPanelDirty = !this.props.panelsPristine[metadataKey];
    if (!item) {
      return (
        <NoContentPanel
          title="Select any asset or collection to view the associated metadata"
          linkText="Learn more"
          linkHref={METADATA}
        />
      );
    }

    if (hasMultipleItemsSelected) {
      return <NoContentPanel title="Select only one item" />;
    }

    return (
      <div className={styles.root}>
        <NavigationPrompt
          when={isMetadataPanelDirty}
          message={UNSAVED_CHANGES_MESSAGE}
        />
        {withPreview && item ? (
          <>
            <div
              className={classNames(styles.item_image, {
                [styles.collection_item_image]:
                  item.object_type === OBJECT_TYPE_COLLECTIONS,
              })}
            >
              <SearchPod
                searchItem={item}
                podSize={{
                  height:
                    item.object_type === OBJECT_TYPE_COLLECTIONS ? 85 : 102,
                  width: 100,
                }}
                imageScrub="off"
                isSelected
                hasPodTitle={false}
              />
            </div>
            <AssetTitle object={item} link={this.props.link} />
          </>
        ) : null}

        <EntityMetadataComponent
          // forms issue. we need to pass the key here so all the previous state is cleared
          key={`${item.id}_${openedVersionId}`}
          aclsObjectId={this.state.viewId}
          aclsType={OBJECT_TYPE_METADATA_VIEWS}
          disabled={!write || !this.state.editMode}
          buttonsDisabled={this.state.submitting}
          isSaveDisabled={!isMetadataPanelDirty}
          visibleMetadata={visible}
          notificationUpdate={this.state.notificationUpdate}
          categoryName={selectedCategoryName}
          onCategoryChange={this.handleCategoryChange}
          itemCategory={this.props.categoryName}
          viewId={this.state.viewId}
          onViewChange={this.handleViewChange}
          metadataValues={values}
          metadataViewIds={share?.metadata_views ?? undefined}
          loading={this.state.loading ?? false}
          submitMetadata={this.submitMetadata}
          onEdit={this.onEdit}
          onCancelEdit={this.onCancelEdit}
          onRefresh={this.onRefresh}
          onFormChange={this.handleFormChange}
          breakpoint={breakpoint}
          componentClass={componentClass}
          buttonsComponent={buttonsComponent}
          onFormClick={this.onEdit}
          objectType={item?.object_type}
          itemId={item.id}
          aiMetadataButtonComponent={AiMetadataButton}
          hideCategorySelect={isShare}
          assetType={item.type ?? undefined}
          submitByCommand
          selectedCategoryObjectType={this.state.selectedCategoryObjectType}
        />
      </div>
    );
  }
}

export default connector(AssetMetadataPanel);
